import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import MarketId from 'components/shared/MarketId';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import Column from 'components/shared/Column';
import ContextMenu from 'components/shared/ContextMenu';
import ModalCancelOrder from 'components/modals/CancelOrder';
import { ORDER_TYPE } from 'constants/translations';
import calculateAverageUnitPrice from 'helpers/calculateAverageUnitPrice';
import { useOpenOrders, useInterval, useIsMobile } from 'helpers/hooks';
import useMarkets from 'hooks/useMarkets';
import useAssets from 'hooks/useAssets';
import { formatCreatedAt } from 'helpers/dates';
import { orderIsNotCancellable, getFillsQuantitySum } from 'helpers/orders';
import { getOptionsListOfAssets } from 'helpers/assets';
import { formatAmount } from '@nbx/frontend-helpers/format';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const renderLink = order => `${microapps.routes.orders.path}/${order.id}`;

export function OrdersOpen(props) {
  const { t } = useTranslation();
  const [asset, setAsset] = useState('All');
  const [viewOptions, setViewOptions] = useState({});
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [cancelRefreshInterval, setCancelRefreshInterval] = useState(null);

  const openOrders = useOpenOrders(props.accountId, asset);
  const markets = useMarkets();
  const assets = useAssets();
  const isMobile = useIsMobile();

  const columnWidths = isMobile ? [3, 4, 4, 1] : [1.5, 2.5, 1, 1.5, 1.5, 2.5, 1.5];

  const endAlignIndex = isMobile ? 1 : 3;

  const headers = isMobile
    ? [t('common_market'), t('common_price'), t('orders_amount_filled'), '']
    : [
        t('common_market'),
        t('common_objects_type'),
        t('common_amount'),
        t('orders_execution_price'),
        t('common_filled'),
        t('common_date'),
        ''
      ];

  useEffect(() => {
    setViewOptions({ All: t('orders_all_assets'), ...getOptionsListOfAssets(assets.data) });
  }, [assets.data, t]);

  const openCancelModal = order => {
    setCancellingOrder(order);
    setCancelModalOpen(true);
  };

  const closeCancelModal = order => {
    setCancelModalOpen(false);
    if (order) {
      setCancelRefreshInterval(3000);
    } else {
      setCancellingOrder(null);
    }
  };

  useEffect(() => {
    if (
      cancellingOrder !== null &&
      !openOrders.refreshing &&
      !generateKeyIndex(openOrders.data).includes(cancellingOrder.id)
    ) {
      setCancellingOrder(null);
      setCancelRefreshInterval(null);
    }
  }, [cancellingOrder, openOrders.data, openOrders.refreshing, openOrders.keyIndex]);

  // If we are waiting on an order to cancel, but not actively refreshing, force a refresh
  useInterval(() => {
    if (cancellingOrder && !openOrders.refreshing) {
      openOrders.refresh();
    }
  }, cancelRefreshInterval);

  const onChangeAsset = e => {
    setAsset(e.target.value);
  };

  const generateKeyIndex = data => {
    return data.map(datum => datum.id);
  };

  const marketDecimals = {};
  if (!markets.loading && !assets.loading) {
    markets.data.forEach(market => {
      marketDecimals[market.pair] = {
        minTickLength: market.minTickLength,
        baseDecimalPrecisionDisplay: assets.data[market.baseAsset]?.decimalPrecisionDisplay ?? 2
      };
    });
  }

  const refetchOnError = () => {
    if (openOrders.error) openOrders.refetch();
    if (markets.error) markets.refetch();
    if (assets.error) assets.refetch();
  };

  const renderItem = (order, index) => {
    const [baseCurrency, quoteCurrency] = order.market.split('-');
    if (index === 6) {
      const isNotCancellable = orderIsNotCancellable(order.events);
      return (
        <ButtonPrimary
          disabled={isNotCancellable}
          title={isNotCancellable ? t('orders_cannot_be_cancelled_until') : undefined}
          onClick={() => openCancelModal(order)}
          styles={[styles.buttonCancel]}>
          {t('common_cancel')}
        </ButtonPrimary>
      );
    }

    const executionPrice = calculateAverageUnitPrice(order);
    return {
      0: <MarketId pair={order.market} iconSize="20" />,
      1: (
        <Text>
          <Text
            styles={[
              styles.renderItemDetail,
              {
                [styles.buy]: order.side === 'BUY',
                [styles.sell]: order.side === 'SELL'
              }
            ]}>
            {order.side === 'BUY' ? t('common_buy').toUpperCase() : t('common_sell').toUpperCase()}
          </Text>{' '}
          {ORDER_TYPE(order.execution.type).toUpperCase()}{' '}
          {order.execution.type === 'LIMIT' ? `${order.execution.price} ${quoteCurrency}` : ''}
        </Text>
      ),
      2: (
        <Text monospaced>
          {formatAmount(order.quantity, marketDecimals[order.market].baseDecimalPrecisionDisplay)}{' '}
          {baseCurrency}
        </Text>
      ),
      3: (
        <Text monospaced>
          {executionPrice
            ? `${formatAmount(
                calculateAverageUnitPrice(order),
                marketDecimals[order.market].minTickLength
              )} ${quoteCurrency}`
            : '-'}
        </Text>
      ),
      4: (
        <Text monospaced>
          {formatAmount(getFillsQuantitySum(order), marketDecimals[order.market].baseDecimalPrecisionDisplay)}{' '}
          {baseCurrency}
        </Text>
      ),
      5: <Text monospaced>{formatCreatedAt(order.events.createdAt)}</Text>
    }[index];
  };

  const renderItemMobile = (order, index) => {
    const isNotCancellable = orderIsNotCancellable(order.events);
    const executionPrice = calculateAverageUnitPrice(order);

    return {
      0: (
        <Column>
          <MarketId pair={order.market} iconSize="20" />
          <span>
            <Text
              styles={[
                styles.renderItemDetail,
                {
                  [styles.buy]: order.side === 'BUY',
                  [styles.sell]: order.side === 'SELL'
                }
              ]}>
              {order.side === 'BUY' ? t('common_buy').toUpperCase() : t('common_sell').toUpperCase()}
            </Text>{' '}
            <Text styles={[styles.renderItemDetail]}>{ORDER_TYPE(order.execution.type).toUpperCase()}</Text>
          </span>
          <Text styles={[styles.renderItemDetail]} monospaced>
            {order.execution.type === 'LIMIT' ? `${order.execution.price}` : ''}
          </Text>
        </Column>
      ),
      1: (
        <Column endAligned>
          <Text monospaced styles={[styles.textBreak]}>
            {executionPrice
              ? formatAmount(calculateAverageUnitPrice(order), marketDecimals[order.market].minTickLength)
              : '-'}
          </Text>
          <Text styles={[styles.renderItemDetail]}>&nbsp;</Text>
        </Column>
      ),
      2: (
        <Column endAligned>
          <Text monospaced>
            {formatAmount(order.quantity, marketDecimals[order.market].baseDecimalPrecisionDisplay)}
          </Text>
          {isNotCancellable ? (
            <Text muted styles={[styles.renderItemDetail]}>
              {t('common_pending')}
            </Text>
          ) : (
            <Text monospaced muted styles={[styles.renderItemDetail]}>
              {formatAmount(
                getFillsQuantitySum(order),
                marketDecimals[order.market].baseDecimalPrecisionDisplay
              )}{' '}
            </Text>
          )}
        </Column>
      ),
      3: !isNotCancellable && (
        <ContextMenu
          appearsVertical="below"
          appearsHorizontal="left"
          options={[
            {
              label: t('common_cancel_order'),
              onClick: () => openCancelModal(order)
            }
          ]}
          buttonStyles={[styles.contextMenuButton]}
          optionButtonStyles={[styles.contextMenuCancel]}
        />
      )
    }[index];
  };

  const loading = openOrders.loading || markets.loading || assets.loading;
  const cancelling = cancellingOrder && !cancelModalOpen;

  return (
    <>
      <SEO title={t('orders_open_orders')} />
      <Table
        data={openOrders.data}
        keyField="id"
        hasNextPage={openOrders.hasNextPage && !openOrders.pausedForEmptyPages}
        pausedForEmptyPages={openOrders.pausedForEmptyPages}
        error={openOrders.error}
        refetchData={refetchOnError}
        isLoading={loading || cancelling}
        fetchNextPage={openOrders.fetchNextPage}
        fetchAllPages={openOrders.fetchAllPages}
        isLoadingNextPage={openOrders.loadingNextPage}
        isLoadingAllPages={openOrders.loadingAllPages}
        setRefreshEndIndex={openOrders.setRefreshEndIndex}
        emptyTitle={t('market_you_dont_have_open_orders')}
        emptyMessage={t('market_as_you_create')}
        endAlignIndex={endAlignIndex}
        view={asset}
        viewOptions={viewOptions}
        onViewChange={onChangeAsset}
        headers={headers}
        columnWidths={columnWidths}
        renderItem={isMobile ? renderItemMobile : renderItem}
        renderLink={renderLink}
        rowHeight={isMobile ? 70 : 55}
        rowStyles={[styles.orderRow]}
        hasActions // hasAction prop always assumes that the last column is clickable
        borders={!isMobile}
      />
      {cancelModalOpen && <ModalCancelOrder handleClose={closeCancelModal} order={cancellingOrder} />}
    </>
  );
}

OrdersOpen.propTypes = {
  accountId: PropTypes.string.isRequired
};

export default OrdersOpen;
